import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { artifactApi } from '../artifacts/api';
import { LIBRARY_CHANGED } from '../artifacts/library-changed';
import { libraryRegistryFor } from '../artifacts/library-registry';
import type { ArtifactRegistry } from '../../../src/artifacts/registry.mjs';
import {
  libraryKindGroups,
  libraryQuery,
  projectLibraryPage,
  type LibraryOpenTarget,
} from '../artifacts/view-models';
import type { LibraryItemVM, LibraryKindFilter, LibraryPanelData, LibrarySegment } from './types';

// The Artifact Library controller: ONE read path, ART's `artifactApi.library`
// (SHARE's authorized listLibrary behind it), projected by ART's
// `projectLibraryPage`. There is no fixture or legacy fallback: when the service
// is unavailable the panel says so and offers a retry. Authorization, filtering,
// counts and cursors are the server's; this hook only holds the person's
// controls, drops responses that are no longer current, and appends pages.

const SEARCH_DEBOUNCE_MS = 300;

export interface ArtifactLibraryState {
  vm: LibraryPanelData;
  /** The original identity (artifact, version, revision, number) of a listed row. */
  openTarget: (id: string) => LibraryOpenTarget | null;
  setSegment: (segment: LibrarySegment) => void;
  setQuery: (query: string) => void;
  setKind: (kind: LibraryKindFilter) => void;
  /** The kind filters on offer: PR1's, plus Apps only when the server's registry carries the App. */
  filters: readonly LibraryKindFilter[] | undefined;
  loadMore: () => void;
  retry: () => void;
}

type Kinds =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; registry?: ArtifactRegistry };
/**
 * An App is never listed as an Artifact (operator 07:20; DES1 spec 5): the Library lists everything BUT Apps and
 * never offers an Apps filter; the Apps place asks for `only` and lists nothing else. Same hook, same read.
 */
export type AppsMode = 'exclude' | 'only';

export function useArtifactLibrary(
  appsMode: AppsMode = 'exclude',
  /** Words the list opens already searching for (the Library's "Apps match" link); none: an empty search. */
  initialQuery = '',
): ArtifactLibraryState {
  // The session this Library opened in: its kind read and every page read ride it.
  const session = useRef(useSessionBoundFetch()).current;
  // What kinds exist is the server's answer (never assumed): groups, query and projection follow it.
  const [kinds, setKinds] = useState<Kinds>({ status: 'loading' });
  const [kindsAttempt, setKindsAttempt] = useState(0);
  const [segment, setSegment] = useState<LibrarySegment>('all');
  const [kind, setKind] = useState<LibraryKindFilter>(appsMode === 'only' ? 'apps' : 'all');
  const [query, setQueryRaw] = useState(initialQuery);
  const [settledQuery, setSettledQuery] = useState(initialQuery);
  const [load, setLoad] = useState<'loading' | 'ready' | 'error'>('loading');
  const [items, setItems] = useState<LibraryItemVM[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const targets = useRef(new Map<string, LibraryOpenTarget>());
  // Every request carries the controls it was made for; an older answer is dropped.
  const generation = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setSettledQuery(query), query === '' ? 0 : SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let current = true;
    setKinds({ status: 'loading' });
    artifactApi
      .listKinds(session)
      .then(
        (value) =>
          current && setKinds({ status: 'ready', registry: libraryRegistryFor(value.kinds) }),
      )
      .catch(() => current && setKinds({ status: 'error' }));
    return () => {
      current = false;
    };
  }, [session, kindsAttempt]);
  const registry = kinds.status === 'ready' ? kinds.registry : undefined;
  const groups = libraryKindGroups(registry);

  const fetchPage = useCallback(
    async (cursor: string | null) => {
      if (kinds.status !== 'ready') return;
      const mine = ++generation.current;
      // A server whose registry has no App kind has no Apps: an honest empty list, not a failed read.
      if (appsMode === 'only' && !groups.apps) {
        setItems([]);
        setNextCursor(null);
        setLoad('ready');
        return;
      }
      const controls = { segment, query: settledQuery, kind };
      try {
        const input = libraryQuery(controls, groups, cursor, registry);
        const page = await artifactApi.library(
          appsMode === 'exclude'
            ? { ...input, kinds: (input.kinds ?? []).filter((kind) => kind !== 'app') }
            : input,
          session,
        );
        if (mine !== generation.current) return;
        const projected = projectLibraryPage(controls, page, {
          groups,
          registry,
          // Pictures are an App version's own, read where that version opens (never an arbitrary URL):
          // the Apps place shows them, and the Library shows a deck's first slide. Other rows show their kind icon.
          thumbnailUrlFor: (item) =>
            appsMode === 'only' || item.presentation === 'deck'
              ? artifactApi.appPictureUrl(item)
              : null,
        });
        if (cursor === null) targets.current = new Map();
        for (const [id, target] of projected.openTargets) targets.current.set(id, target);
        setItems((current) => {
          const seen = new Set(cursor === null ? [] : current.map((item) => item.id));
          return [
            ...(cursor === null ? [] : current),
            // The Apps place lists kind = App only: a deck built like an App reads as a deck here, and decks live in the Library.
            ...projected.data.items.filter(
              (item) => !seen.has(item.id) && (appsMode !== 'only' || item.kind === 'app'),
            ),
          ];
        });
        setNextCursor(projected.nextCursor);
        setLoad('ready');
      } catch {
        if (mine !== generation.current) return;
        if (cursor === null) setItems([]);
        setLoad('error');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [segment, settledQuery, kind, kinds, appsMode],
  );

  // A save confirmed elsewhere (the candidate beside this page) re-reads the same list, keeping the rows on
  // screen until the new answer lands (no flash of "loading").
  useEffect(() => {
    const changed = () => void fetchPage(null);
    window.addEventListener(LIBRARY_CHANGED, changed);
    return () => window.removeEventListener(LIBRARY_CHANGED, changed);
  }, [fetchPage]);

  useEffect(() => {
    if (kinds.status === 'error') {
      setItems([]);
      setLoad('error');
      return;
    }
    setLoad('loading');
    void fetchPage(null);
    return () => {
      generation.current++;
    };
  }, [fetchPage, reload]);

  return {
    vm: {
      load,
      segment,
      query,
      kind,
      items,
      hasMore: nextCursor !== null,
    },
    openTarget: (id) => targets.current.get(id) ?? null,
    setSegment,
    setQuery: setQueryRaw,
    setKind,
    // No Apps filter anywhere: Apps are never a Library kind.
    filters: undefined,
    loadMore: () => {
      if (nextCursor !== null) void fetchPage(nextCursor);
    },
    retry: () => {
      // A failed kind read is retried too; a ready one is not asked again.
      if (kinds.status === 'error') setKindsAttempt((n) => n + 1);
      setReload((n) => n + 1);
    },
  };
}
